import React from "react";
import { researchAreas } from "../../constants/types";

/**
 * PaperResearchClassification Component
 * 
 * Step 4 of the paper submission wizard.
 * Handles research area classification selections.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.formData - Current form data
 * @param {Function} props.handleChange - Form field change handler
 */
const PaperResearchClassification = ({
  formData,
  handleChange
}) => {
  // Use formData for category selections instead of local state
  const selectedCategories = {
    researchArea1: formData.researchArea1Category || "",
    researchArea2: formData.researchArea2Category || "",
    researchArea3: formData.researchArea3Category || ""
  };

  /**
   * Creates category options for the first level of selection
   * @returns {Array} Array of option elements with research area categories
   */
  const createCategoryOptions = () => {
    return Object.keys(researchAreas).map((category, index) => (
      <option key={`category-${index}`} value={category}>
        {category}
      </option>
    ));
  };

  /**
   * Creates topic options for the second level of selection based on selected category
   * @param {string} selectedCategory - The selected research area category
   * @returns {Array} Array of option elements with topics for the selected category
   */
  const createTopicOptions = (selectedCategory) => {
    if (!selectedCategory || !researchAreas[selectedCategory]) {
      return [];
    }
    
    return researchAreas[selectedCategory].map((topic, index) => (
      <option key={`topic-${index}`} value={topic}>
        {topic}
      </option>
    ));
  };

  /**
   * Handle category selection change
   * @param {Event} event - The select change event
   * @param {string} fieldName - The field name (researchArea1, researchArea2, etc.)
   */
  const handleCategoryChange = (event, fieldName) => {
    const selectedCategory = event.target.value;
    
    // Update the category in formData
    handleChange({
      target: {
        name: `${fieldName}Category`,
        value: selectedCategory
      }
    });
    
    // Clear the corresponding topic selection
    handleChange({
      target: {
        name: fieldName,
        value: ""
      }
    });
  };

  /**
   * Handle topic selection change
   * @param {Event} event - The select change event
   */
  const handleTopicChange = (event) => {
    handleChange(event);
  };

  return (
    <div className="row">
      <div className="col-12">
        <h4 className="mb-4">
          <i className="bi bi-tags me-2"></i>
          Area &amp; Topic of Submitted Manuscript
        </h4>

        {/* Research Areas Section */}
        <div className="mb-4">

          {/* Primary MFS Classification Codes (Required) */}
          <div className="mb-3">
            <div className="mb-4">
              <label htmlFor="researchArea1Category" className="form-label">
                Primary Area <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                id="researchArea1Category"
                name="researchArea1Category"
                value={selectedCategories.researchArea1 || ""}
                onChange={(e) => handleCategoryChange(e, "researchArea1")}
                required
              >
                <option value="">Select category...</option>
                {createCategoryOptions()}
              </select>
              
              {selectedCategories.researchArea1 && (
                <div className="mt-2 ms-3">
                  <label htmlFor="researchArea1" className="form-label">
                    Topic <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    id="researchArea1"
                    name="researchArea1"
                    value={formData.researchArea1 || ""}
                    onChange={handleTopicChange}
                    required
                  >
                    <option value="">Select topic...</option>
                    {createTopicOptions(selectedCategories.researchArea1)}
                  </select>
                </div>
              )}
            </div>
            
            <div className="mb-4">
              <label htmlFor="researchArea2Category" className="form-label">
                Secondary Area <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                id="researchArea2Category"
                name="researchArea2Category"
                value={selectedCategories.researchArea2 || ""}
                onChange={(e) => handleCategoryChange(e, "researchArea2")}
                required
              >
                <option value="">Select category...</option>
                {createCategoryOptions()}
              </select>
              
              {selectedCategories.researchArea2 && (
                <div className="mt-2 ms-3">
                  <label htmlFor="researchArea2" className="form-label">
                    Topic <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    id="researchArea2"
                    name="researchArea2"
                    value={formData.researchArea2 || ""}
                    onChange={handleTopicChange}
                    required
                  >
                    <option value="">Select topic...</option>
                    {createTopicOptions(selectedCategories.researchArea2)}
                  </select>
                </div>
              )}
            </div>
            
            <div className="mb-4">
              <label htmlFor="researchArea3Category" className="form-label">
                Tertiary Area <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                id="researchArea3Category"
                name="researchArea3Category"
                value={selectedCategories.researchArea3 || ""}
                onChange={(e) => handleCategoryChange(e, "researchArea3")}
                required
              >
                <option value="">Select category...</option>
                {createCategoryOptions()}
              </select>
              
              {selectedCategories.researchArea3 && (
                <div className="mt-2 ms-3">
                  <label htmlFor="researchArea3" className="form-label">
                    Topic <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    id="researchArea3"
                    name="researchArea3"
                    value={formData.researchArea3 || ""}
                    onChange={handleTopicChange}
                    required
                  >
                    <option value="">Select topic...</option>
                    {createTopicOptions(selectedCategories.researchArea3)}
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>

         {/* Information Box */}
        <div className="alert alert-warning" role="alert">
          <p className="mb-0">
            <i className="text-danger bi bi-exclamation-triangle me-2"></i> 
            Please select the <strong>subject areas &amp; topics</strong> that best describe your paper and rank them by relevance (Primary, Secondary, Tertiary). Your selections help us assign your paper to an appropriate session and group-related presentations.
          </p>
        </div>

        {/* Required fields notice */}
        <div className="mb-3">
          <p className="text-muted">
            <span className="text-danger">*</span> Required
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaperResearchClassification;
